import React, { useEffect } from 'react';
import { SITE } from '../content/tools';

export const ROBOTS_INDEX =
  'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
export const ROBOTS_NOINDEX = 'noindex, nofollow';

export interface SEOProps {
  /** Full page title, including any site suffix. */
  title: string;
  description: string;
  /** Site-relative path for the canonical URL, e.g. "/base64". Omit on pages that must not be canonicalised. */
  path?: string;
  keywords?: string;
  /** Defaults to ROBOTS_INDEX. */
  robots?: string;
  image?: string;
  type?: string;
}

type MetaAttribute = 'name' | 'property';

function upsertMeta(attribute: MetaAttribute, key: string, content?: string) {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.head.querySelector<HTMLMetaElement>(selector);

  if (!content) {
    if (existing) {
      existing.remove();
    }
    return;
  }

  const tag = existing ?? document.createElement('meta');
  if (!existing) {
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(href?: string) {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!href) {
    if (existing) {
      existing.remove();
    }
    return;
  }

  const tag = existing ?? document.createElement('link');
  if (!existing) {
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Keeps document.head in step during client-side navigation.
 *
 * The HTML that search engines and link unfurlers actually read is written at
 * build time by `scripts/prerender.mjs` from the same `src/content/tools.json`
 * values this component reads, so the two cannot drift. This component only has
 * to update the tags when the SPA renders a new route without a page load.
 */
const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path,
  keywords,
  robots,
  image,
  type = 'website',
}) => {
  useEffect(() => {
    const canonical = path ? `${SITE.origin}${path === '/' ? '/' : path}` : undefined;
    const socialImage = image ?? SITE.defaultImage;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', robots ?? ROBOTS_INDEX);
    upsertCanonical(canonical);

    upsertMeta('property', 'og:site_name', SITE.name);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', socialImage);
    upsertMeta('property', 'og:locale', SITE.locale);

    upsertMeta('name', 'twitter:card', SITE.twitterCard);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', socialImage);
  }, [title, description, path, keywords, robots, image, type]);

  return null;
};

export default SEO;
